/* The explorer's shapes: what a saved view is, what the log offers, what a plot draws. */

export interface SeriesRef {
  topic: string;
  instance: number;
  field: string;
  label?: string;
  color?: string;
}

export interface InlineData {
  t: number[];
  series: Record<string, (number | null)[]>;
  y_label?: string;
  reference_lines?: { value: number; label: string; color?: string }[];
  /** Draw as bars on a non-time x axis (the gyro spectrum). */
  bars?: boolean;
  /** Set when x is not time; the plot then stays out of the time cursor. */
  x_label?: string;
  /** Spans the model marked on its own chart. */
  highlights?: { start_s: number; end_s: number; label?: string; color?: string }[];
}

export interface PlotSpec {
  id: string;
  title?: string;
  kind?: 'line' | 'step';
  series: SeriesRef[];
  y?: { min?: number; max?: number };
  /** The model's computed charts and the Overview; never saved. */
  inline?: InlineData;
}

export interface Tab {
  name: string;
  columns: 1 | 2 | 3;
  plots: PlotSpec[];
  builtin?: 'overview' | 'analyst';
}

export interface ChartLayout {
  version: 1;
  tabs: Tab[];
}

export interface ViewContext {
  profile: string | null;
  tab: string;
  window: [number, number] | null;
  plots: { title: string; series: string[] }[];
}

/* ── Saved profiles (GET /analysis/layouts) ── */

export interface LayoutOwner {
  id: string;
  firstname?: string;
  lastname?: string;
}

export interface LayoutRecord {
  id: string;
  name: string;
  shared: boolean;
  layout: ChartLayout;
  mine: boolean;
  owner?: LayoutOwner;
  created_at?: string;
  updated_at?: string;
}

export interface LayoutList {
  layouts: LayoutRecord[];
  default_id: string | null;
}

/* ── The log's inventory (GET /analysis/log-fields/:logId) ── */

export type FieldKind = 'float' | 'int' | 'bool';

export interface InventoryField {
  name: string;
  kind: FieldKind;
}

export interface InventoryTopic {
  name: string;
  instance: number;
  rows: number;
  rate_hz: number;
  fields: InventoryField[];
}

export interface Inventory {
  log_id: string;
  duration_s: number;
  topics: InventoryTopic[];
}

/* ── One series request (GET /analysis/series/:logId) ── */

export interface SeriesRequest {
  topic: string;
  instance: number;
  fields: string[];
  t0?: number;
  t1?: number;
  points?: number;
  /** rows keeps whole rows paired (the map); the default keeps spikes. */
  mode?: 'minmax' | 'rows';
}

export interface SeriesResponse {
  topic: string;
  instance: number;
  t: number[];
  fields: Record<string, (number | null)[]>;
  rows_total: number;
  rows_in_window: number;
  bucketed: boolean;
  t0: number;
  t1: number;
}

/* ── What a plot draws ── */

/** x first, then one column per series in the plot's order. */
export type PlotColumns = [number[], ...(number | null | undefined)[][]];

export interface PlotData {
  status: 'loading' | 'ready' | 'error';
  columns?: PlotColumns;
  error?: string;
  /** True when the box summarized the rows to fit the plot's width. */
  bucketed?: boolean;
}

/* ── Overlays from the flight report ── */

export interface ModeBand {
  start_s: number;
  end_s: number;
  label: string;
}

export interface TimeMarker {
  t: number;
  label: string;
  color: string;
}

/* ── The model's charts, as the stream sends them ── */

export interface InlineAgentChart {
  kind?: undefined;
  title: string;
  y_label?: string;
  timestamps_s: number[];
  series: Record<string, (number | null)[]>;
  reference_lines?: { value: number; label: string; color?: string }[];
  highlights?: { start_s: number; end_s: number; label?: string; color?: string }[];
}

export interface SeriesAgentChart {
  kind: 'series';
  title: string;
  y_label?: string;
  series: SeriesRef[];
}

export type AgentChart = InlineAgentChart | SeriesAgentChart;

/** A dragged field travels as JSON under this type. */
export const SERIES_DRAG_TYPE = 'application/x-firefleet-series';
